import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Row } from 'react-bootstrap';
import AOS from 'aos';
import 'aos/dist/aos.css';
import ContactForm from '../components/ContactForm';
import GoogleMapEmbed from '../components/GoogleMap';
import TimeList from '../components/TimeList';
import DateSelect from '../components/DateSelect';
import { useWebContent } from '../hooks/useWebContent';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import api from '../services/api';
import Swal from 'sweetalert2';
import { useUpdateHeadTags } from '../hooks/useUpdateHeadTags';
import { useAdverts } from '../hooks/useAdverts';
import AdvertPopup from '../components/AdvertPopup';

// Fallback data (unchanged)
const fallbackHeadingSection = {
  heading_text: 'CONTACT US',
  title: 'We are here to help',
  small_description:
    'Need support? Or if you have a query? Please get in touch with us. Our team will revert back quickly for support.',
};

const fallbackContactInfoSection = {
  title: 'Get in touch with us.',
  heading_text: 'Contact information',
  small_description: 'Small Description',
  message_us_text: 'Message us',
  message_us_description: 'Fill up the form below, our team will get back soon',
  ticket_heading: 'Generate Ticket',
  ticket_description: 'Need support or have a query? Submit a support ticket.',
  ticket_button_text: 'Generate Ticket Now',
  ticket_image: '',
  sliders: [
    {
      image: '',
      title: 'Email Us',
      description: 'info@vidyakasam.com',
    },
    {
      image: '',
      title: 'Call Us',
      description: '+91-93172-10020',
    },
    {
      image: '',
      title: 'Visit Us',
      description: 'KAPIL VILL DHAR, C/O, PO, Patnaon, Himachal Pradesh 177023',
    },
  ],
  google_maps_location: '<div>No map available</div>',
};

const fallbackStates = [
  {
    state_id: 1,
    full_name: 'Andhra Pradesh',
  },
  {
    state_id: 12,
    full_name: 'Haryana',
  },
  {
    state_id: 13,
    full_name: 'Himachal Pradesh',
  },
];

// Validation schema (unchanged)
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  phone: Yup.string()
    .matches(/^[0-9+\-\s()]*$/, 'Phone number is invalid')
    .min(10, 'Phone number must be at least 10 digits')
    .required('Phone number is required'),
  date: Yup.string()
    .required('Please select a date'),
  time: Yup.string()
    .required('Please select a time'),
});

const Contact = () => {
  const [showModal, setShowModal] = useState(false);
  const { data: webContent, isLoading, isError } = useWebContent();
  const [isCooldown, setIsCooldown] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { headers, headersLoading } = useUpdateHeadTags('contactus');
  const location = useLocation();

  // Extract email from query parameter or navigation state
  const queryParams = new URLSearchParams(location.search);
  const prefilledEmail = queryParams.get('email') || location.state?.email || '';

  // Extract sections with fallback
  const headingSection = webContent?.dataset?.data?.['contact-us-heading-section']?.json_data || fallbackHeadingSection;
  const contactInfoSection = webContent?.dataset?.data?.['contact-us-contact-info-section']?.json_data || fallbackContactInfoSection;
  const states = webContent?.dataset?.data?.['contact-us-states'] || fallbackStates;
  const sliders = contactInfoSection.sliders || [];

  // Advert popup logic
  const { data: advertsData } = useAdverts({ position_key: 'contact_us_popup', ad_format: 'popup', useWebContent: true });
  const [showAdvert, setShowAdvert] = useState(false);
  const [shownOnce, setShownOnce] = useState(false);

  useEffect(() => {
    AOS.init({ once: true });
    // Open modal automatically if email is present
    if (prefilledEmail) {
      setShowModal(true);
    }
    if (!isLoading && advertsData?.dataset?.data?.length && !shownOnce) {
      setShowAdvert(true);
      setShownOnce(true);
    }
  }, [prefilledEmail, isLoading, advertsData, shownOnce]);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  // Initial form values with prefilled email
  const initialValues = {
    name: '',
    email: prefilledEmail,
    phone: '',
    date: '',
    time: '',
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!executeRecaptcha) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'reCAPTCHA not loaded. Please try again.',
        timer: 3000,
        showConfirmButton: false,
      });
      setSubmitting(false);
      return;
    }

    Swal.fire({
      title: 'Creating Ticket...',
      text: 'Please wait while we process your ticket.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const recaptchaToken = await executeRecaptcha('create_ticket');
      const payload = {
        ...values,
        recaptchaToken,
      };
      const response = await api.post('/web-content/create-ticket', payload);
      Swal.close();
      await Swal.fire({
        icon: 'success',
        title: 'Ticket Created!',
        text: 'Your ticket has been created successfully.',
        timer: 2000,
        showConfirmButton: false,
      });
      resetForm();
      setIsCooldown(true);
      setTimeout(() => {
        setIsCooldown(false);
      }, 5000);
    } catch (error) {
      Swal.close();
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'An error occurred while creating the ticket';
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: errorMessage,
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAdvert = () => setShowAdvert(false);
  function getRandomAdvert(advertData) {
    const arr = advertData?.dataset?.data;
    if (Array.isArray(arr) && arr.length > 0) {
      const idx = Math.floor(Math.random() * arr.length);
      return arr[idx];
    }
    return undefined;
  }
  const popupAdvert = getRandomAdvert(advertsData);

  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <img
          src="https://stg-admin.gkcloud.in/storage/settings/backend_favicon_1739509041.png"
          alt="Loading..."
          style={{
            width: '100px',
            height: 'auto',
            animation: 'pulse 1.5s infinite',
          }}
        />
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  if (isError) {
    return <div>Error loading content. Please try again later.</div>;
  }

  return (
    <>
      {/* Banner Section */}
      <section
        className="contact-banner"
        style={{
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          margin: '0px auto 0',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: '180px',
          paddingBottom: '0px',
          minHeight: '450px',
        }}
      >
        <div className="floating-dots">
          {[...Array(10)].map((_, i) => (
            <div className="dot" key={i}></div>
          ))}
        </div>
        <Container>
          <Row className="align-items-center">
            <div className="col-lg-12">
              <div
                className="section_title top_content text-center"
                data-aos="fade-up"
                data-aos-duration="1500"
                data-aos-delay="300"
              >
                <span className="title_badge mb-1">{headingSection.heading_text}</span>
                <h2>{headingSection.title}</h2>
                <p className="mb-0">{headingSection.small_description}</p>
                <ul className="banner-nav">
                  <li>
                    <Link to="/">Home</Link>
                  </li>
                  <li>
                    <span>»</span>
                  </li>
                  <li>
                    <Link to="/contact">Contact us</Link>
                  </li>
                </ul>
              </div>
            </div>
          </Row>
        </Container>
      </section>

      {/* Contact Information Section */}
      <section className="Contact-information">
        <Container>
          <div className="contact-content">
            <Row>
              <div className="col-lg-6">
                <div className="contact-left">
                  <div className="message-heading">
                    <h4 style={{ textAlign: 'left' }}>{contactInfoSection.heading_text}</h4>
                    <p className="mb-0 get-touch">{contactInfoSection.title}</p>
                  </div>
                  <ul className="contact-description">
                    {sliders.length > 0 ? (
                      sliders.map((slider, index) => (
                        <li key={index}>
                          <div className="leftt-img">
                            <img src={slider.image} alt={slider.title} onError={(e) => (e.target.src = '')} />
                          </div>
                          <div className="right-text">
                            <span>{slider.title}</span>
                            {slider.title.includes('Email') ? (
                              <a href={`mailto:${slider.description}`}>{slider.description}</a>
                            ) : slider.title.includes('Call') ? (
                              <a href={`tel:${slider.description}`}>{slider.description}</a>
                            ) : (
                              <p className="mb-0">{slider.description}</p>
                            )}
                          </div>
                        </li>
                      ))
                    ) : (
                      <>
                        <li>
                          <div className="leftt-img">
                            <img src="" alt="Email" />
                          </div>
                          <div className="right-text">
                            <span>Email Us</span>
                            <a href="mailto:support@gmail.com">info@vidyakasam.com</a>
                          </div>
                        </li>
                        <li>
                          <div className="leftt-img">
                            <img src="" alt="Phone" />
                          </div>
                          <div className="right-text">
                            <span>Call Us</span>
                            <a href="tel:+919317210020">+91-93172-10020</a>
                          </div>
                        </li>
                        <li>
                          <div className="leftt-img">
                            <img src="" alt="Visit" />
                          </div>
                          <div className="right-text">
                            <span>Visit Us</span>
                            <p className="mb-0">KAPIL VILL DHAR, C/O, PO, Patnaon, Himachal Pradesh 177023</p>
                          </div>
                        </li>
                      </>
                    )}
                  </ul>
                  <div className="generate-ticket">
                    <div className="ticket-img">
                      <img
                        src={contactInfoSection.ticket_image}
                        alt={contactInfoSection.ticket_heading}
                        onError={(e) => (e.target.src = '')}
                      />
                    </div>
                    <div className="ticket-heading">
                      <h4 className="mb-0">{contactInfoSection.ticket_heading}</h4>
                      <p>{contactInfoSection.ticket_description}</p>
                    </div>
                    <button type="button" className="contact-btn" onClick={openModal}>
                      {contactInfoSection.ticket_button_text}
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <ContactForm contactInfoSection={contactInfoSection} states={states} />
              </div>
            </Row>

            {/* Modal for Ticket Form */}
            {showModal && (
              <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  zIndex: 1050,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div className="modal-dialog modal-dialog-centered modal-md ticket-popup">
                  <div className="modal-content" style={{ background: '#fff' }}>
                    <div className="modal-header">
                      <h5 className="modal-title">Create Ticket</h5>
                      <div className="close-btn">
                        <button
                          type="button"
                          className="btn-close"
                          onClick={closeModal}
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>
                    <div className="modal-body p-4">
                      <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                      >
                        {({ isSubmitting, values, setFieldValue }) => (
                          <Form>
                            <div className="form-group">
                              <Field
                                type="text"
                                name="name"
                                placeholder="Name"
                                className="form-control"
                                disabled={isSubmitting || isCooldown}
                              />
                              <ErrorMessage name="name" component="div" className="text-danger" />
                            </div>
                            <div className="form-group">
                              <Field
                                type="email"
                                name="email"
                                placeholder="Email"
                                className="form-control"
                                disabled={isSubmitting || isCooldown}
                              />
                              <ErrorMessage name="email" component="div" className="text-danger" />
                            </div>
                            <div className="form-group">
                              <Field
                                type="text"
                                name="phone"
                                placeholder="Phone"
                                className="form-control"
                                disabled={isSubmitting || isCooldown}
                              />
                              <ErrorMessage name="phone" component="div" className="text-danger" />
                            </div>
                            <DateSelect
                              selectedDate={values.date}
                              onDateSelect={(date) => setFieldValue('date', date)}
                            />
                            <ErrorMessage name="date" component="div" className="text-danger" />
                            <TimeList
                              selectedTime={values.time}
                              onTimeSelect={(time) => setFieldValue('time', time)}
                            />
                            <ErrorMessage name="time" component="div" className="text-danger" />
                            <div className="ticket-btn">
                              <button
                                type="submit"
                                className="btn get-btn w-100"
                                disabled={isSubmitting || isCooldown}
                              >
                                {isSubmitting
                                  ? 'Creating...'
                                  : isCooldown
                                  ? 'Please wait...'
                                  : 'Create Ticket'}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </section>

      <GoogleMapEmbed mapEmbed={contactInfoSection.google_maps_location} />

      <AdvertPopup
        advert={popupAdvert}
        show={showAdvert}
        onClose={handleCloseAdvert}
        position_key="contact_us_popup"
        useWebContent={true}
      />
    </>
  );
};

export default Contact;

